import {ActivatedRouteSnapshot} from '@angular/router';
import {ReportKind} from './reports/report-kind';

import {ALL_FUNDS, GarfieldFundNode} from '../components/garfield-funds-selector/garfield-fund-node-model';
import {GarfieldPivotDisplayUnit} from './pivot-table/garfield-pivot-display-unit';
import {GovTypeCode} from '../services/api/fit-api/models/gov-type-code';
import {ALL_COUNTIES} from '../components/counties-selector/counties-model';
import {ALL_ACCOUNTING_BASES} from '../services/api/fit-api/models/accounting-basis';

/**
 * Provides typed values for Route Parameters
 * @deprecated now encapsulated within RouteOptions classes
 */
export class RouteParser {
	constructor(
		private route: ActivatedRouteSnapshot,
	) {}
	//#region report values
	get MCAGs(): Array<string> | undefined {
		return this.route.paramMap.get('mcags')?.split(',');
	}

	get report(): ReportKind | undefined {
		const maybeReportString = this.route.paramMap.get('report');
		if (maybeReportString) {
			if (Object.values(ReportKind).includes(maybeReportString as ReportKind)) {
				return maybeReportString as ReportKind;
			}
			// note: do not throw an error if report not found, simple return undefined (this way, a report that is not found can be handled by the report guard appropriately)
		}

		return undefined;
	}
	//#endregion

	//#region option values
	get displayYear(): number | undefined {
		const maybeYearString = this.route.paramMap.get('displayYear');
		if (maybeYearString) {
			return Number.parseInt(maybeYearString);
		}
		return undefined;
	}

	// note: fund node is written to url in fundCategory|fundType|fund pattern and nulls are removed.
	// e.g. fund type = Enterprise, 2,2
	get fundNode() {
		const maybeFundNodeString = this.route.paramMap.get('fundNode');
		if (maybeFundNodeString) {
			const [maybeCategory, type, fund] = maybeFundNodeString.split(',');

			// if category is not a number, test for 'All funds', if not that its null - throw error (there's never a valid case where category is null)
			const category = isNaN(parseInt(maybeCategory, 10)) ? maybeCategory === ALL_FUNDS ? ALL_FUNDS : null : parseInt(maybeCategory, 10);
			const maybeType = isNaN(parseInt(type, 10)) ? null : parseInt(type, 10);
			const maybeFund = fund === "null" ? null : fund;

			if (category) {
				return new GarfieldFundNode(category, maybeType, maybeFund);
			} else {
				throw new Error(`Not a correct fundNode value: ${maybeFundNodeString}`);
			}

		} else {
			return undefined;
		}
	}

	get excludeInternalServiceFunds(): boolean | undefined{
		return this.parseBoolean('excludeInternalServiceFunds');
	}

	get displayUnit(): GarfieldPivotDisplayUnit | undefined {
		const maybeDisplayUnitString = this.route.paramMap.get('displayUnit');
		if (maybeDisplayUnitString) {
			return maybeDisplayUnitString as GarfieldPivotDisplayUnit;
		}
		return undefined;
	}

	get showAccountNumbers(): boolean | undefined {
		return this.parseBoolean('showAccountNumbers');
	}

	get byExpenditureObjects(): boolean | undefined {
		return this.parseBoolean('byExpenditureObjects');
	}

	get filterByName() {
		const maybeFilterNameString = this.route.paramMap.get('filterByName');
		if (maybeFilterNameString) {
			return maybeFilterNameString;
		}
		return undefined;
	}

	get govTypeCode() {
		const maybeGovTypeCodeString = this.route.paramMap.get('govTypeCode');
		if (maybeGovTypeCodeString) {
			return maybeGovTypeCodeString as GovTypeCode;
		}
		return undefined;
	}

	get showLocalGovernmentsOnly() {
		return this.parseBoolean('showLocalGovernmentsOnly');
	}

	get counties() {
		const maybeCountyCodesString = this.route.paramMap.get('counties');
		if (maybeCountyCodesString) {
			if (maybeCountyCodesString === ALL_COUNTIES) {
				return ALL_COUNTIES;
			}

			const countyCodes = maybeCountyCodesString.split(',').map(c => parseInt(c));
			if (countyCodes.length > 0) {
				return countyCodes;
			} else {
				throw new Error(`Not correct county code values: ${maybeCountyCodesString}`);
			}

		}
		return undefined;
	}

	get accountingBases() {
		const maybeAccountingBasesString = this.route.paramMap.get('accountingBases');
		if (maybeAccountingBasesString) {
			if (maybeAccountingBasesString === ALL_ACCOUNTING_BASES) {
				return ALL_ACCOUNTING_BASES;
			}

			const accountingBasesIds = maybeAccountingBasesString.split(',').map(c => parseInt(c));
			if (accountingBasesIds.length > 0) {
				return accountingBasesIds;
			} else {
				throw new Error(`Not correct accounting bases values: ${maybeAccountingBasesString}`);
			}

		}
		return undefined;
	}

	// get years(): BoundedRange | undefined {
	// 	const maybeYearsString = this.route.paramMap.get('years');
	// 	if (maybeYearsString) {
	// 		const values = maybeYearsString.split(',');
	// 		const numbers = values.map(v => Number.parseInt(v));
	// 		// todo handle NaN and incorrect length
	// 		return [numbers[0], numbers[1]];
	// 	}
	// 	return undefined;
	// }
	//#endregion

	parseBoolean = (param: string) => {
		const maybeParamStringValue = this.route.paramMap.get(param);
		if (maybeParamStringValue) {
			if (maybeParamStringValue === 'true') {
				return true;
			}

			if (maybeParamStringValue === 'false') {
				return false;
			}

			throw new Error(`Not a correct ${param} value: ${maybeParamStringValue}`);
		} else {
			return undefined;
		}
	}
}
